import { useState, useRef, useCallback, useEffect } from 'react';
import { modalStack } from '../components/mobile/modal/modalStack';

export interface UsePullToRefreshOptions {
    onRefresh?: () => Promise<void> | void;
    disabled?: boolean;
    threshold?: number; // 70px
    maxPull?: number;   // 100px
}

export type PullDirection = 'down' | 'up' | null;

export function usePullToRefresh({
    onRefresh,
    disabled = false,
    threshold = 70,
    maxPull = 100
}: UsePullToRefreshOptions = {}) {
    const [pullDistance, setPullDistance] = useState(0);
    const [pullDirection, setPullDirection] = useState<PullDirection>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{
        y: number;
        x: number;
        atTop: boolean;
        atBottom: boolean;
        directionLocked: boolean;
    }>({ y: 0, x: 0, atTop: false, atBottom: false, directionLocked: false });

    const handleRefresh = useCallback(async (dir: PullDirection) => {
        setIsRefreshing(true);
        setPullDistance(threshold);
        setPullDirection(dir);

        try {
            // 1. Disparar evento global de sincronización para useSyncState
            window.dispatchEvent(new CustomEvent('klyd_sync_refresh'));

            // 2. Invocar callback específico de recarga de la pantalla si existe
            if (onRefresh) {
                await onRefresh();
            }

            // Buffer visual mínimo para visualización fluida del giro
            await new Promise(resolve => setTimeout(resolve, 550));
        } catch (err) {
            console.error('[usePullToRefresh] Error durante la recarga de datos:', err);
        } finally {
            setIsRefreshing(false);
            setPullDistance(0);
            setPullDirection(null);
        }
    }, [onRefresh, threshold]);

    const handleTouchStart = useCallback((e: React.TouchEvent | TouchEvent) => {
        if (disabled || isRefreshing) return;

        // Si hay un modal, hoja, diálogo o menú abierto en modalStack, no activar
        if (modalStack.hasModals()) return;

        const touch = e.touches[0];
        if (!touch) return;

        // Comprobar si el toque ocurrió dentro de un modal o elemento aislado
        const target = e.target as HTMLElement | null;
        if (target && target.closest('[data-kl-sheet="true"], [data-kl-fullmodal="true"], [data-kl-dialog="true"], [data-kl-menu="true"], #kl-modal-root')) {
            return;
        }

        const container = containerRef.current;
        const scrollTop = container ? container.scrollTop : (window.scrollY || window.pageYOffset || 0);
        const scrollHeight = container ? container.scrollHeight : document.documentElement.scrollHeight;
        const clientHeight = container ? container.clientHeight : window.innerHeight;

        const isShortScreen = scrollHeight <= clientHeight + 4;
        const atTop = scrollTop <= 1;
        const atBottom = isShortScreen || (scrollTop + clientHeight >= scrollHeight - 3);

        touchStartRef.current = {
            y: touch.clientY,
            x: touch.clientX,
            atTop,
            atBottom,
            directionLocked: false
        };
    }, [disabled, isRefreshing]);

    const handleTouchMove = useCallback((e: React.TouchEvent | TouchEvent) => {
        if (disabled || isRefreshing) return;
        if (modalStack.hasModals()) return;

        const touch = e.touches[0];
        if (!touch) return;

        const start = touchStartRef.current;
        const diffY = touch.clientY - start.y;
        const diffX = touch.clientX - start.x;

        // Si el gesto es predominantemente horizontal, ignorar para permitir swiping
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10 && !start.directionLocked) {
            return;
        }

        const container = containerRef.current;
        const scrollTop = container ? container.scrollTop : (window.scrollY || 0);
        const scrollHeight = container ? container.scrollHeight : document.documentElement.scrollHeight;
        const clientHeight = container ? container.clientHeight : window.innerHeight;
        const isShortScreen = scrollHeight <= clientHeight + 4;

        // Gesto hacia abajo (desde arriba)
        if (diffY > 0 && (start.atTop && scrollTop <= 1)) {
            start.directionLocked = true;
            const distance = Math.min(maxPull, diffY * 0.5);
            setPullDistance(distance);
            setPullDirection('down');

            if (e.cancelable && distance > 6) {
                e.preventDefault();
            }
            return;
        }

        // Gesto hacia arriba (desde el fondo)
        if (diffY < 0 && (start.atBottom || isShortScreen)) {
            const isCurrentlyAtBottom = isShortScreen || (scrollTop + clientHeight >= scrollHeight - 3);
            if (isCurrentlyAtBottom) {
                start.directionLocked = true;
                const distance = Math.min(maxPull, Math.abs(diffY) * 0.5);
                setPullDistance(distance);
                setPullDirection('up');

                if (e.cancelable && distance > 6) {
                    e.preventDefault();
                }
                return;
            }
        }

        // Si ya no cumple la condición de borde
        if (pullDistance > 0 && !isRefreshing) {
            setPullDistance(0);
            setPullDirection(null);
        }
    }, [disabled, isRefreshing, maxPull, pullDistance]);

    const handleTouchEnd = useCallback(async () => {
        if (disabled || isRefreshing) return;

        const dir = pullDirection;
        if (pullDistance >= threshold && dir) {
            await handleRefresh(dir);
        } else {
            setPullDistance(0);
            setPullDirection(null);
        }
    }, [disabled, isRefreshing, pullDistance, pullDirection, threshold, handleRefresh]);

    // Vincular event listeners pasivos o no-pasivos al contenedor
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e: TouchEvent) => handleTouchStart(e);
        const onTouchMove = (e: TouchEvent) => handleTouchMove(e);
        const onTouchEnd = () => handleTouchEnd();

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        el.addEventListener('touchcancel', onTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Estilos de transformación del contenido
    const contentTransform = pullDistance > 0
        ? pullDirection === 'down'
            ? `translateY(${pullDistance}px)`
            : `translateY(-${pullDistance}px)`
        : undefined;

    const contentTransition = isRefreshing
        ? 'transform var(--kl-dur-open, 280ms) var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))'
        : pullDistance === 0
            ? 'transform var(--kl-dur-close, 200ms) var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))'
            : 'none';

    return {
        containerRef,
        pullDistance,
        pullDirection,
        isRefreshing,
        contentStyle: {
            transform: contentTransform,
            transition: contentTransition
        },
        threshold,
        maxPull
    };
}
